import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import catLogo from '../assets/ic_cat.svg';

export default function NotFound() {
  const [isJumping, setIsJumping] = useState(false);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const scoreRef = useRef(0);
  const characterRef = useRef<HTMLDivElement>(null);
  const obstacleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  const handleStart = () => {
    setIsPlaying(true);
    setGameOver(false);
    setScore(0);
    setFinalScore(0);
  };

  const handleJump = useCallback((e?: React.MouseEvent | TouchEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!isJumping && isPlaying && !gameOver) {
      setIsJumping(true);
      setTimeout(() => setIsJumping(false), 500);
    }
  }, [isJumping, isPlaying, gameOver]);

  // 碰撞檢測
  useEffect(() => {
    if (!isPlaying || gameOver) return;

    const checkCollision = () => {
      const character = characterRef.current?.getBoundingClientRect();
      const obstacle = obstacleRef.current?.getBoundingClientRect();

      if (!character || !obstacle) return;

      if (
        character.right > obstacle.left &&
        character.left < obstacle.right &&
        character.bottom > obstacle.top
      ) {
        handleGameOver(); // 在碰撞時調用 handleGameOver
      }
    };

    const gameLoop = setInterval(checkCollision, 10);
    return () => clearInterval(gameLoop);
  }, [isPlaying, gameOver]);

  // 鍵盤控制
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.key === 'ArrowUp') {
        e.preventDefault();
        handleJump();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleJump]);

  // 添加動畫結束處理
  const handleAnimationEnd = useCallback(() => {
    if (isPlaying && !gameOver) {
      const obstacle = obstacleRef.current;
      if (obstacle) {
        // 重置動畫
        obstacle.style.animation = 'none';
        obstacle.offsetHeight; // 觸發重排
        obstacle.style.animation = 'obstacle 2s linear';
        // 增加分數
        setScore(prev => prev + 1);
      }
    }
  }, [isPlaying, gameOver]);

  // 添加觸控事件監聽
  useEffect(() => {
    const gameContainer = document.querySelector('.game-container');
    if (gameContainer) {
      gameContainer.addEventListener('touchstart', handleJump);
      return () => {
        gameContainer.removeEventListener('touchstart', handleJump);
      };
    }
  }, [handleJump]);

  // 處理遊戲結束
  const handleGameOver = useCallback(() => {
    setGameOver(true);
    setIsPlaying(false);
    setFinalScore(scoreRef.current);
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <Header onSearch={() => {}} />
      
      <div className="flex-1 bg-[#1a1625] pt-16">
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center text-white">
            <h1 className="text-8xl font-bold mb-4">404</h1>
            <p className="text-2xl mb-8">Oops! Page not found</p>
            
            {/* Game Container */}
            <div className="bg-[#13111d] rounded-lg p-8 mb-4 game-container">
              <div className="relative h-32 border-b-2 border-white/20">
                {/* Character */}
                <div 
                  ref={characterRef}
                  className={`absolute bottom-0 left-16 w-8 h-8 transition-transform duration-500
                    ${isJumping ? '-translate-y-16' : 'translate-y-0'}`}
                >
                  <img 
                    src={catLogo} 
                    alt="Cat"
                    className={`w-full h-full transition-transform ${isJumping ? 'rotate-12' : ''}`}
                    draggable="false"
                  />
                </div>
                
                {/* Obstacle */}
                {isPlaying && !gameOver && (
                  <div 
                    ref={obstacleRef}
                    onAnimationEnd={handleAnimationEnd}
                    className="absolute bottom-0 right-0 w-4 h-8 bg-red-500 animate-obstacle"
                  />
                )}
              </div>
            </div>

            {/* Game Controls */}
            <div className="mb-8 space-y-4">
              {!isPlaying ? (
                <div className="space-y-4">
                  {gameOver && (
                    <div className="text-xl mb-4">
                      Game Over! Final Score: <span className="text-purple-400">{finalScore}</span>
                    </div>
                  )}
                  <button
                    onClick={handleStart}
                    className="px-6 py-2 bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {gameOver ? 'Try Again' : 'Start Game'}
                  </button>
                </div>
              ) : (
                <div className="text-xl">Score: {score}</div>
              )}
              
              <div className="text-sm text-gray-400">
                Press Space or Up Arrow to jump
              </div>
            </div>

            <Link 
              to="/" 
              className="inline-block px-8 py-3 bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
} 